import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'rgb(239, 231, 221)',
        display: 'flex',
        alignItems: 'center',
        py: 3
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: isMobile ? '90vh' : '80vh',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              position: 'sticky',
              top: 0,
              backgroundColor: 'inherit',
              zIndex: 10,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}
          >
            <Typography variant="h6">
              Privacy Policy – PropertyPincoder
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Effective Date: June 9, 2025
            </Typography>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ overflowY: 'auto', px: 2, pb: 2, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              1. Introduction
            </Typography>
            <Typography variant="body2" paragraph>
              This Privacy Policy explains how <strong>PropertyPincoder</strong>, operated by
              JK Constructions And Interiors, collects, uses, and protects your personal
              information when you access our website
              (<strong>https://propertypincoder.com</strong>) or mobile application.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              2. Information We Collect
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Personal Information"
                  secondary="Name, email address, phone number"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Location Information"
                  secondary="City, area, and property location"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Property Details"
                  secondary="Buy, rent, lease listings, images, pricing, and descriptions"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Account Information"
                  secondary="Login credentials and profile details for agents, builders, and owners"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Technical Information"
                  secondary="Device information, IP address, and usage logs"
                />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              3. How We Use Your Information
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Provide buy, rent, and lease services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Enable agents, builders, and owners to manage listings" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Facilitate communication between buyers, renters, and sellers" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Improve platform performance and user experience" />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              4. Data Sharing
            </Typography>
            <Typography variant="body2" paragraph>
              We may share your data with agents, builders, owners, or trusted service providers
              strictly for service delivery. We do not sell your personal data to third parties.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              5. Third-Party Links
            </Typography>
            <Typography variant="body2" paragraph>
              PropertyPincoder may contain links to third-party websites. We are not responsible
              for their privacy practices. Please review their policies separately.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              6. Data Security
            </Typography>
            <Typography variant="body2" paragraph>
              We use industry-standard security measures, including secure servers and encryption
              in transit, to protect your data.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              7. Your Rights
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Access or update your personal information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Request deletion of your account and data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Withdraw consent for marketing communications" />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              8. Data Retention
            </Typography>
            <Typography variant="body2" paragraph>
              We retain your information only as long as necessary to provide services or
              comply with legal obligations.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              9. Children’s Privacy
            </Typography>
            <Typography variant="body2" paragraph>
              PropertyPincoder services are not intended for individuals under 18 years of age.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              10. Changes to This Policy
            </Typography>
            <Typography variant="body2" paragraph>
              We may update this Privacy Policy from time to time. Changes will be effective
              upon posting on https://propertypincoder.com.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              11. Contact Us
            </Typography>
            <Typography variant="body2" paragraph>
              For questions, data access, or deletion requests, contact us at
              <strong> jayajyosh@gmail.com</strong>.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              12. Governing Law
            </Typography>
            <Typography variant="body2" paragraph>
              This Privacy Policy is governed by the laws of India.
            </Typography>
          </Box>

          {/* Footer */}
          <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.12)' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/')}
              >
                I have read and accept the Privacy Policy
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
